import { HttpClient } from "@utils/httpClient";
import type {
  ActivateBody,
  ConfigDiff,
  CreateDraftBody,
  DiffAgainst,
  DraftResponse,
  ImportDraftBody,
  ImportResponse,
  PatchDraftBody,
  PluginConfigVersion,
  ServiceList,
  ServiceSchema,
  VersionPage,
} from "@models/mappingConfig";

const BASE_URL = import.meta.env.VITE_MAPPING_API_BASE_URL;

const MISSING_BASE_URL_MESSAGE =
  "Missing required environment variable: VITE_MAPPING_API_BASE_URL. " +
  "Please check your .env file and ensure the Mapping API URL is set.";

const seg = encodeURIComponent;

/**
 * Mapping plugin-config admin API (`<mapping base>/plugin-configs`).
 *
 * Every path segment is encoded: version ids contain `#` (`v#20261012T...Z`),
 * which would otherwise end the path and turn the rest into a fragment.
 */
export class MappingConfigApi {
  private readonly http: HttpClient | null;

  constructor(baseUrl: string | undefined = BASE_URL, http?: HttpClient) {
    // Same deferred failure as mappingApi: a missing URL breaks only this feature,
    // never the whole module at import time.
    this.http = http ?? (baseUrl ? new HttpClient(`${baseUrl.replace(/\/+$/, "")}/plugin-configs`) : null);
  }

  private request<T>(endpoint: string, init: RequestInit = {}): Promise<T> {
    if (!this.http) return Promise.reject(new Error(MISSING_BASE_URL_MESSAGE));
    return this.http.makeAuthenticatedRequest<T>(endpoint, init);
  }

  private pluginPath(service: string, pluginId: string): string {
    return `/${seg(service)}/${seg(pluginId)}`;
  }

  private versionPath(service: string, pluginId: string, versionId: string): string {
    return `${this.pluginPath(service, pluginId)}/versions/${seg(versionId)}`;
  }

  listServices(signal?: AbortSignal): Promise<ServiceList> {
    return this.request<ServiceList>("/services", { method: "GET", signal });
  }

  getSchema(service: string, signal?: AbortSignal): Promise<ServiceSchema> {
    return this.request<ServiceSchema>(`/services/${seg(service)}/schema`, { method: "GET", signal });
  }

  listVersions(
    service: string,
    pluginId: string,
    page: { limit?: number; cursor?: string | null } = {},
    signal?: AbortSignal,
  ): Promise<VersionPage> {
    const params = new URLSearchParams();
    params.set("limit", String(page.limit ?? 50));
    if (page.cursor) params.set("cursor", page.cursor);
    return this.request<VersionPage>(`${this.pluginPath(service, pluginId)}/versions?${params}`, {
      method: "GET",
      signal,
    });
  }

  getActive(service: string, pluginId: string, signal?: AbortSignal): Promise<PluginConfigVersion> {
    return this.request<PluginConfigVersion>(`${this.pluginPath(service, pluginId)}/active`, { method: "GET", signal });
  }

  getVersion(service: string, pluginId: string, versionId: string, signal?: AbortSignal): Promise<PluginConfigVersion> {
    return this.request<PluginConfigVersion>(this.versionPath(service, pluginId, versionId), { method: "GET", signal });
  }

  getDiff(
    service: string,
    pluginId: string,
    versionId: string,
    against: DiffAgainst = "active",
    signal?: AbortSignal,
  ): Promise<ConfigDiff> {
    const query = new URLSearchParams({ against });
    return this.request<ConfigDiff>(`${this.versionPath(service, pluginId, versionId)}/diff?${query}`, {
      method: "GET",
      signal,
    });
  }

  createDraft(service: string, pluginId: string, body: CreateDraftBody): Promise<DraftResponse> {
    return this.request<DraftResponse>(`${this.pluginPath(service, pluginId)}/versions`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  patchDraft(service: string, pluginId: string, versionId: string, body: PatchDraftBody): Promise<DraftResponse> {
    return this.request<DraftResponse>(this.versionPath(service, pluginId, versionId), {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  }

  importDraft(service: string, pluginId: string, body: ImportDraftBody): Promise<ImportResponse> {
    const form = new FormData();
    form.append("file", body.file);
    form.append("importer", body.importer);
    form.append("mode", body.mode);
    form.append("comment", body.comment);
    if (body.base_version_id) form.append("base_version_id", body.base_version_id);
    return this.request<ImportResponse>(`${this.pluginPath(service, pluginId)}/versions/import`, {
      method: "POST",
      body: form,
    });
  }

  activate(service: string, pluginId: string, versionId: string, body: ActivateBody): Promise<PluginConfigVersion> {
    return this.request<PluginConfigVersion>(`${this.versionPath(service, pluginId, versionId)}/activate`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  deleteDraft(service: string, pluginId: string, versionId: string): Promise<void> {
    return this.request<void>(this.versionPath(service, pluginId, versionId), { method: "DELETE" });
  }
}

export const mappingConfigApi = new MappingConfigApi();
